import { boxFixedPriceCentsOf, tt } from '@agos/shared'

/**
 * The burger box: a set of slots, each filled from one category, sold as one thing.
 *
 * **Measured: `box_legacy` is active on this data** — four slots (burger, príloha, dressing, nápoje),
 * `triggerCategoryId` 3, and every slot `required: false`. So a customer can add an empty-ish box, and
 * the emptiness rule below is the one that matters rather than the required-ness.
 *
 * FB-190 is the manager's version of the same feature and its lesson carries: legacy checks each slot's
 * own `required` flag before checking whether the box has anything in it at all, so a box of four
 * optional slots could be added completely empty — a line on the order, at some price, containing
 * nothing. **The empty check comes first here.**
 *
 * A box is priced as the sum of what is in it. That is not the kombo's rule and the difference is real:
 * a kombo is three pizzas *instead of* one, a box is four items *together*.
 */

export interface BoxSlot {
  id: string
  label: string
  categoryId: number | null
  required: boolean
  /** Items the operator pinned into the slot; they are in the box whether or not the customer chooses. */
  presetItemIds: number[]
}

export interface BoxConfig {
  id: string
  name: string
  description: string
  active: boolean
  /** The category whose page offers the box. `null` means it is offered nowhere. */
  triggerCategoryId: number | null
  hideProductImages: boolean
  /**
   * Sold for this, whatever its parts cost - §10A.107, the owner's item E.
   *
   * In **cents**, because everything the basket prices is in cents (FB-056). `null` means *sum of
   * the parts*, which is every box today; a stored zero is read as `null`, because in a free-form
   * blob they cannot be told apart and a free box is a gift rule.
   *
   * **The server is the authority.** `ServerPricing` applies the same split from the same stored
   * configuration; this is here so the screen shows what the bill will say, not so the client can
   * decide a price.
   */
  fixedPriceCents: number | null
  slots: BoxSlot[]
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function id(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function boxConfigsOf(payload: unknown): BoxConfig[] {
  if (!Array.isArray(payload)) return []
  const configs: BoxConfig[] = []
  for (const raw of payload) {
    if (typeof raw !== 'object' || raw === null) continue
    const row = raw as Record<string, unknown>
    const slots = Array.isArray(row.slots) ? row.slots : []
    configs.push({
      id: text(row.id),
      name: text(row.name),
      description: text(row.description),
      active: row.active === true,
      triggerCategoryId: id(row.triggerCategoryId),
      hideProductImages: row.hideProductImages === true,
      fixedPriceCents: boxFixedPriceCentsOf(row.fixedPrice),
      slots: slots
        .filter((slot): slot is Record<string, unknown> => typeof slot === 'object' && slot !== null)
        .map((slot) => ({
          id: text(slot.id),
          label: text(slot.label),
          categoryId: id(slot.categoryId),
          required: slot.required === true,
          presetItemIds: Array.isArray(slot.presetItems)
            ? slot.presetItems.map((item) => id(item)).filter((item): item is number => item !== null)
            : [],
        }))
        // A slot with no id cannot be filled or removed; it is a heading with a dropdown under it.
        .filter((slot) => slot.id !== ''),
    })
  }
  return configs
}

/** The box a category offers, or `null` — only one, and only when it is switched on. */
/**
 * Every box this category offers — §9.200.
 *
 * **`boxFor` below ends in `.find`, and that was the whole of the owner's report.** The payload has
 * always been an array, `boxConfigsOf` has always parsed all of it, and a second active box on the
 * same category was fetched, parsed, held and never drawn — configurable, savable, invisible.
 *
 * In the server's order. A box with no slots is not offered: an empty panel is a control that
 * cannot be completed.
 */
export function boxesFor(
  configs: readonly BoxConfig[],
  categoryId: number | null,
): BoxConfig[] {
  if (categoryId === null) return []
  return configs.filter(
    (config) => config.active && config.triggerCategoryId === categoryId && config.slots.length > 0,
  )
}

/** The first of them, for a caller that wants one answer. */
export function boxFor(configs: readonly BoxConfig[], categoryId: number | null): BoxConfig | null {
  return boxesFor(configs, categoryId)[0] ?? null
}

/** What a slot may be filled with: the products in its category, still on the menu. */
export function optionsFor<
  T extends {
    id: number
    categoryId?: number
    active?: boolean
    visibleOnWeb?: boolean
    orderable?: boolean
  },
>(slot: BoxSlot, products: readonly T[]): T[] {
  if (slot.categoryId === null) return []
  return products.filter(
    (product) =>
      product.categoryId === slot.categoryId &&
      product.active !== false &&
      product.visibleOnWeb !== false &&
      // §9.155 — a slot must not offer what the kitchen has run out of. The card for that product is
      // greyed on the menu, but a box slot builds its own list and would have offered it happily;
      // the server refuses the line (§10A.77), so without this the customer picks it, builds the
      // whole box, and is refused at checkout. Same late refusal §9.156 removed from the manager.
      product.orderable !== false,
  )
}

export type BoxSelection = Readonly<Record<string, number | null>>

export const EMPTY_BOX: BoxSelection = {}

/**
 * Why the box cannot be added yet, or `''`.
 *
 * **Empty first.** FB-190: legacy checks each slot's `required` flag and, with every slot optional —
 * which is the measured configuration — lets a box with nothing in it onto the order.
 */
export function boxProblem(config: BoxConfig, selection: BoxSelection): string {
  const chosen = config.slots.filter((slot) => (selection[slot.id] ?? null) !== null)
  const preset = config.slots.filter((slot) => slot.presetItemIds.length > 0)
  if (chosen.length === 0 && preset.length === 0) return tt('Do boxu zatiaľ nič nie je vybrané.')

  const missing = config.slots.filter(
    (slot) => slot.required && (selection[slot.id] ?? null) === null && slot.presetItemIds.length === 0,
  )
  if (missing.length > 0) {
    return `${tt('Chýba')}: ${missing.map((slot) => slot.label).join(', ')}`
  }
  return ''
}

export interface BoxLine {
  productId: number
  slotLabel: string
}

/**
 * What goes in the basket: one line per item, all sharing a group id.
 *
 * Not one composed line, because a box is real products — the kitchen makes a burger and a portion of
 * fries, and the order has to name both. The group id is what lets the basket remove them as one thing,
 * which is `removeBox`'s whole reason for existing.
 */
export function boxLines(config: BoxConfig, selection: BoxSelection): BoxLine[] {
  const lines: BoxLine[] = []
  for (const slot of config.slots) {
    for (const preset of slot.presetItemIds) {
      lines.push({ productId: preset, slotLabel: slot.label })
    }
    const chosen = selection[slot.id] ?? null
    if (chosen !== null) lines.push({ productId: chosen, slotLabel: slot.label })
  }
  return lines
}

/**
 * The group id for one box in the basket.
 *
 * Derived from the box and what is in it, so adding the same box twice makes one line of two rather
 * than two groups that look identical and cannot be told apart — the same reasoning as FB-C25's kombo,
 * and the same mistake avoided.
 */
export function boxGroupId(config: BoxConfig, selection: BoxSelection): string {
  const filling = config.slots
    .map((slot) => `${slot.id}=${selection[slot.id] ?? ''}`)
    .join(',')
  return `${config.id}:${filling}`
}
